import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import isEmpty from 'lodash-es/isEmpty';
import { combineLatest, EMPTY, forkJoin, map, of } from 'rxjs';
import { catchError, concatMap, finalize, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ContractFilterStatus } from '../../../enums/contract-filter-status.enum';
import { ApiHeaders } from '../../../models/api-query.model';
import { ContractConversionDto, ContractCP, ContractDetail } from '../../../models/contract-model';
import { ModalService, NotificationService } from '../../../services';
import { AppFacadeService } from '../../../services/app-facade.service';
import { ConsumerPortalApiService } from '../../../services/consumer-portal-api.service';
import { ContractService } from '../../../services/contract.service';
import { selectRouteParamsContractCustomerId, selectRouteParamsContractIds } from '../../app-wide/config/config.selectors';
import { setContractCurrency } from '../../app-wide/currencycode/currencycode.actions';
import { ComponentsToLoad } from '../../../enums/components-to-load.enum';
import {
    clearContractinStore,
    ContractActionTypes,
    failLoadContractDetails,
    fetchContractDetail,
    fetchContracts,
    loadContractAction,
    setContractDetailData,
    setNoMoreRecord,
    setSelectedContracts,
    SetContractDetailLineItemsData,
    setContractDetailPaymentData,
    fetchContractDetailLineItems,
    fetchContractDetailPayment,
    convertQuoteToReservation,
    setQuoteConversionInProgress
} from './contract.actions';
import { getPageNumber } from './contract.selectors';

@Injectable()
export class ContractEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(ModalService);
    private readonly contractService = inject(ContractService);
    private readonly notificationService = inject(NotificationService);

    onPaymentOpen$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ContractActionTypes.MultiPaymentContinue),
                map(() => this.modalService.open('payment-modal'))
            ),
        { dispatch: false }
    );

    onPaymentClose$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ContractActionTypes.PaymentWindowClosed),
                map(() => this.modalService.close('payment-modal'))
            ),
        { dispatch: false }
    );

    fetchContracts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContracts),
            tap(() => this.appFacadeService.setLoading(true)),
            withLatestFrom(this.store.pipe(select(getPageNumber)), this.appFacadeService.getPageSizeValues()),
            switchMap(([action, currentPageNumber, pageSizeValues]) => {
                const { selectedPageSize, previousPageSize } = pageSizeValues;

                const { customerId, status = [], startDate, endDate, search } = action;
                let pageNumber = currentPageNumber;
                if (previousPageSize !== selectedPageSize) {
                    this.store.dispatch(clearContractinStore());
                    pageNumber = 1;
                }
                const headers: ApiHeaders = this.prepareHeaders(pageNumber, customerId, status, startDate, endDate, search, Number(selectedPageSize));

                return this.consumerPortalApi.get<ContractCP>(`customer/${customerId}/contracts`, { headers }).pipe(
                    switchMap((contracts: ContractCP[]) => {
                        if (!contracts || contracts.length === 0) {
                            return of(setNoMoreRecord({ record: true }));
                        }

                        /**
                         * Don't try to translate original status
                         */
                        // contracts = contracts.map((contract: ContractCP) => ({
                        //     ...contract,
                        //     status: this.translateService.instant(contract.status)
                        // }));

                        return of(loadContractAction({ contracts, pageSize: Number(selectedPageSize) }), setContractCurrency({ currencyCode: contracts[0]?.CurrencyCode || '' }));
                    }),
                    catchError(() => of(setNoMoreRecord({ record: true }))),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                );
            })
        )
    );

    fetchContractDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContractDetail),
            tap(() => this.appFacadeService.setLoading(true)),
            switchMap(({ forPayment, customerId, contractId }) =>
                combineLatest([this.store.pipe(select(selectRouteParamsContractCustomerId)), this.store.pipe(select(selectRouteParamsContractIds))]).pipe(
                    take(1),
                    map(([customerIdFromState, contractIdsFromState]) => ({
                        customerId: customerId ?? customerIdFromState,
                        contractIds: contractId ? [contractId] : contractIdsFromState,
                        forPayment
                    }))
                )
            ),
            switchMap(({ forPayment, customerId, contractIds }) => {
                if (!customerId || !contractIds || contractIds.length === 0) {
                    return of(failLoadContractDetails({ error: 'Missing customerId or contractIds' }));
                }

                // Fetch all contracts in parallel
                const fetchObservables = contractIds.map(id => this.contractService.getContractsDetails(customerId, id));

                return forkJoin(fetchObservables).pipe(
                    concatMap((detailsArray: ContractDetail[][]) => {
                        // Null-safety: Filter out empty/null responses
                        const validDetails = detailsArray.filter(details => details && details.length > 0);

                        if (validDetails.length === 0) {
                            this.notificationService.error(this.translateService.instant('NoContractDetailsFound'));
                            return of(failLoadContractDetails({ error: 'No contract details found' }));
                        }

                        const contracts: ContractCP[] = validDetails.map(details => this.contractService.convertContractDetailToContractCP(details[0]));

                        const actions = [];
                        actions.push(loadContractAction({ contracts }));

                        if (forPayment) {
                            const fetchedContractIds = contracts.map(c => c.contractId);
                            actions.push(setSelectedContracts({ selectedContracts: fetchedContractIds }));
                        }

                        // Set first contract detail (legacy single-contract behavior)
                        actions.push(setContractDetailData({ data: validDetails[0][0] }));

                        return of(...actions);
                    }),
                    catchError((error: HttpErrorResponse) => of(failLoadContractDetails({ error: error?.message }))),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                );
            })
        )
    );

    loadLineItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContractDetailLineItems),
            switchMap(({ customerId, contractId }) =>
                this.contractService.getContractsDetailsLineItems(customerId, contractId).pipe(
                    map(lineItems => SetContractDetailLineItemsData({ data: lineItems })),
                    catchError(() => EMPTY) // optional: silently fail
                )
            )
        )
    );

    loadPaymentHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContractDetailPayment),
            switchMap(({ customerId, contractId }) =>
                this.contractService.getContractsDetailsPaymentHistory(customerId, contractId).pipe(
                    map(payments => setContractDetailPaymentData({ data: payments })),
                    catchError(() => EMPTY)
                )
            )
        )
    );

    convertQuoteToReservation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(convertQuoteToReservation),
            tap(() => this.store.dispatch(setQuoteConversionInProgress({ inProgress: true }))),
            withLatestFrom(this.appFacadeService.getActiveTab()),
            switchMap(([{ customerId, contractId }, currentActiveTab]) => {
                if (!this.appFacadeService.featureToggleService.isAvailable('autoConvertQuotes')) {
                    this.store.dispatch(setQuoteConversionInProgress({ inProgress: false }));
                    this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                    this.appFacadeService.openContract(contractId);
                    return EMPTY;
                }

                this.appFacadeService.setActiveTab(ComponentsToLoad.QuoteConversion);

                return this.contractService.convertQuoteToReservation(customerId, contractId).pipe(
                    switchMap((result: ContractConversionDto) => {
                        if (result.success && result.contract) {
                            this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                            this.appFacadeService.openContract(result.contract.Id);

                            return of(setQuoteConversionInProgress({ inProgress: false }));
                        } else {
                            this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                            this.appFacadeService.openContract(contractId);
                            this.store.dispatch(setQuoteConversionInProgress({ inProgress: false }));
                            return EMPTY;
                        }
                    }),
                    catchError(error => {
                        this.appFacadeService.openContract(contractId);
                        this.notificationService.error(this.translateService.instant(error.message));

                        this.store.dispatch(setQuoteConversionInProgress({ inProgress: false }));
                        return EMPTY;
                    })
                );
            })
        )
    );

    private prepareHeaders(pageNumber: number, customerId: string, status?: string[], startDate?: string, endDate?: string, search?: string, pageSize?: number): ApiHeaders {
        const columns = [
            'Id',
            'CustomerId',
            'Name',
            'Status',
            'ContractStatus',
            'StartDateTime',
            'EndDateTime',
            'PONumber',
            'AdditionalFields',
            'Addresses',
            'GrandTaxTotal',
            'GrandTotal',
            'AmountDue',
            'DepotId',
            'LineItems'
        ];
        let headers: ApiHeaders = {
            /* eslint-disable @typescript-eslint/naming-convention */
            'x-columns': JSON.stringify(columns)
        };

        if (pageNumber && customerId) {
            headers = {
                ...headers,
                /* eslint-disable @typescript-eslint/naming-convention */
                'x-paging': JSON.stringify({ page: pageNumber, pageSize: pageSize })
            };
        }

        const finalFilter = this.buildFilters(status, search, startDate, endDate);

        if (finalFilter.length > 0) {
            headers = {
                ...headers,
                /* eslint-disable @typescript-eslint/naming-convention */
                'x-filter': JSON.stringify(finalFilter)
            };
        }

        return headers;
    }

    private buildFilters(status?: string[], search?: string, startDate?: string, endDate?: string): unknown[] {
        const filters = [];

        if (this.appFacadeService.versionToggleService.isBasicVersion()) {
            filters.push({ field: 'Status', type: '!=', value: ContractFilterStatus.Closed });
            // AB#89333: Removed AmountDue > 0 filter to allow $0 balance contracts to display
            // Users need to see open contracts regardless of balance amount
        }

        if (search && !isEmpty(search)) {
            const searchFilters = [];

            if (this.appFacadeService.featureToggleService.isAvailable('textSearchMultipleFilters')) {
                searchFilters.push(
                    { field: 'PONumber', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.CustomerJobNumber', type: 'LIKE', value: search },
                    { field: 'Name', type: 'LIKE', value: search }
                );

                // #94144 Syrinx - Id has to be number but sent as string and LIKE doesn't work
                if (this.appFacadeService.featureToggleService.isSyrinx()) {
                    if (!isNaN(Number(search))) {
                        searchFilters.push({ field: 'Id', type: '==', value: Number(search).toString() });
                    }
                } else {
                    searchFilters.push({ field: 'Id', type: 'LIKE', value: search });
                }
            } else {
                searchFilters.push({ field: 'Name', type: 'LIKE', value: search });
            }
            filters.push({ field: '', type: 'OR', value: searchFilters });
        }

        if (status && status?.length > 0) {
            const statusFilters = status?.map(statusItem => ({
                field: 'Status',
                type: '==',
                value: statusItem
            }));
            filters.push({ field: '', type: 'OR', value: statusFilters });
        }

        if (startDate) {
            filters.push({ field: 'StartDateTime', type: '>=', value: new Date(new Date(startDate).setUTCHours(0, 0, 0, 0)) });
        }
        if (endDate) {
            filters.push({ field: 'EndDateTime', type: '<=', value: new Date(new Date(endDate).setUTCHours(23, 59, 59, 999)) });
        }

        return filters;
    }
}
